<template>
  <div
    class="app-sub-menu"
    :class="{ 'app-sub-menu--active': isActive }"
  >
    <el-submenu
      v-if="isVisibleMainRoute"
      :index="mainRoute.name"
    >
      <template slot="title">
        <el-tooltip
          class="item"
          effect="dark"
          :open-delay="200"
          :content="mainRoute.meta.title"
          placement="right"
          :disabled="children.length > 1"
        >
          <router-link
            :is="mainRoute.meta.disabled ? 'span' : 'router-link'"
            :to="mainRoute.redirect ?? mainRoute.path"
            class="app-sub-menu__icon-wrap"
            :class="{
              'app-sub-menu__icon-wrap--disabled': mainRoute.meta.disabled && children.length > 1,
            }"
          >
            <fa-icon
              v-if="mainRoute.meta.icon"
              :icon="mainRoute.meta.icon"
              class="app-sub-menu__fa-icon"
            />

            <img
              v-else-if="mainRoute.meta.customIcon"
              :src="require(`@/assets/svg/${mainRoute.meta.customIcon}`)"
              alt="Icon Rik SIde Menu"
              class="app-sub-menu__icon"
            />
          </router-link>
        </el-tooltip>
      </template>

      <template v-if="children.length > 1">
        <el-menu-item
          v-for="(child, childIndex) in children"
          class="app-sub-menu__item"
          :key="childIndex"
          :index="child.name"
          :class="{ 'app-sub-menu__item--disabled': child.meta.disabled }"
          :disabled="child.meta.disabled"
        >
          <router-link
            class="app-sub-menu__link"
            :is="child.meta.disabled ? 'span' : 'router-link'"
            :to="child.path"
          >
            {{ child.meta.title }}
          </router-link>
        </el-menu-item>
      </template>
    </el-submenu>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'
import { TRouteConfig } from '../utils/router'
import { useRoute } from 'vue-router/composables'
import { useFeatureFlagsStore } from '../stores/FeatureFlags.store'

export default defineComponent({
  name: 'AppSubMenu',

  props: {
    route: {
      type: Object as PropType<TRouteConfig>,
      required: true,
    },
  },

  setup(props) {
    const route = useRoute()
    const featuresStore = useFeatureFlagsStore()

    const mainRoute = computed(() => {
      if (children.value.length && !props.route.redirect) return children.value[0]
      return props.route
    })

    const isActive = computed(() => {
      return route.path === mainRoute.value.path
    })

    const children = computed(() => {
      const { allowedRouts } = props.route.meta

      const result = (props.route.children ?? []).filter((e) => {
        if (!checkRouteVisible(e)) return false

        if (allowedRouts?.length) {
          return allowedRouts.includes(e.name)
        }

        return true
      })
      return result
    })

    const isVisibleMainRoute = computed(() => {
      return checkRouteVisible(mainRoute.value) || children.value.length !== 0
    })

    const checkRouteVisible = (route: TRouteConfig): boolean => {
      return (
        route.meta.visible !== false && featuresStore.can(route.meta.devFlag) && route.path !== ''
      )
    }

    return {
      children,
      isActive,
      mainRoute,
      isVisibleMainRoute,
      checkRouteVisible,
    }
  },
})
</script>

<style lang="scss">
.app-sub-menu {
  &--active {
    .app-sub-menu__icon-wrap {
      background-color: rgba(0, 0, 0, 0.06);
    }
  }

  .el-submenu {
    height: 50px;

    &.is-active {
      .app-sub-menu__icon-wrap {
        background-color: rgba(0, 0, 0, 0.06);
      }
    }

    &__title {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 !important;
      cursor: pointer;

      .el-submenu__icon-arrow::before {
        display: none;
      }

      &:hover {
        background-color: transparent !important;

        .app-sub-menu__icon-wrap {
          background-color: rgba(0, 0, 0, 0.06);
        }
      }
    }
  }

  &__icon-wrap {
    display: flex !important;
    align-items: center;
    justify-content: center;
    width: 40px !important;
    height: 40px !important;
    overflow: auto !important;
    visibility: visible !important;
    font-size: 16px;
    border-radius: 16px;

    &--disabled {
      cursor: not-allowed;
    }
  }

  &__fa-icon {
    height: 1em;
    color: #9e9b98;
  }

  &__icon {
    width: 30px;
    height: 30px;
  }

  &__item {
    cursor: pointer;

    &.is-active,
    &:not(.app-sub-menu__item--disabled):hover {
      background-color: #f1f0ed !important;
    }
  }

  &__link {
    display: inline-block;
    width: 100%;
    height: 100%;
    color: #000;
    text-decoration: none;

    // &:hover {
    //   background-color: #f1f0ed;
    // }
  }
}
</style>
